import React, { useCallback, useState } from 'react'
import { Dropdown } from 'react-bootstrap'

import { convertUTCtoMSK } from '../../../../../../../utils'
import { FileDownloadButton } from '../../../../../../../components/FileDownloadButton/FileDownloadButton'
import { MatchingFile } from '../../../../core/_models'
import { BillStatusBadge } from '../../../../components/common/BillStatusBadge'
import { ReceiptUploadButton } from '../../../../components/common/ReceiptUploadButton'
import { useReceiptActions } from '../../../../components/common/useReceiptActions'
import { DisputeAutoCancelCountdown } from './DisputeAutoCancelCountdown'

import { KTIcon } from '../../../../../../../_metronic/helpers'
import { RowKebabMenu, RowKebabSlot } from '../../../../../../common/RowKebabMenu'
import { TruncatedValue } from '../../../../../../common/TruncatedValue'
import { ReleaseDepositChunkModal } from '../../../../components/common/ReleaseDepositChunkModal'
import { DisputeRecord, DisputeReasonLookupItem } from '../../core/response.types'
import { DISPUTE_STATUS_BADGE, DISPUTE_STATUS_LABEL } from '../../core/consts'
import {
  isPayoutDispute,
  renderAmount,
  renderAutoSituation,
  renderChunkHold,
  renderComment,
  renderCreatedBy,
  renderCulprits,
  renderDisputeType,
  renderResolvedBy,
} from '../../core/render'
import { PayoutTokenLink } from '../../../../../../common/PayoutTokenLink'
import { DisputeReasonCell } from './DisputeReasonCell'

interface Props {
  dispute: DisputeRecord
  canManageBills: boolean
  onFilesChange: (disputeId: number, files: MatchingFile[]) => void
  onResolveSuccess: (dispute: DisputeRecord) => void
  onReject: (dispute: DisputeRecord) => void
  onChunkReleased: (disputeId: number, heldUntil: string) => void
  reasons: DisputeReasonLookupItem[]
  onDisputeUpdated: (dispute: DisputeRecord) => void
}

export const DisputeRow: React.FC<Props> = ({
  dispute,
  canManageBills,
  onFilesChange,
  onResolveSuccess,
  onReject,
  onChunkReleased,
  reasons,
  onDisputeUpdated,
}) => {
  const files = dispute.files ?? []
  const bill = files[0]

  const [showReleaseModal, setShowReleaseModal] = useState(false)
  // Своего флага «уже освободили» у строки нет: и колонка «Освобождение выплаты», и наличие
  // действия считаются из одного момента, который после освобождения обновляет владелец списка.
  // Отдельный локальный флаг разошёлся бы с колонкой и заодно сделал бы отложенное освобождение
  // неотзываемым — пока момент не наступил, удержание ещё живо и срок можно задать заново.
  const canReleaseChunk = dispute.canReleaseChunk === true

  // Открытый диспут морозит операции с чеком: бэкенд отвечает 409 TICKET_HAS_OPEN_DISPUTE,
  // пока решение по спору не принято (CPAYMENT-1615). Кнопки решения показываем только
  // на закрытых диспутах, иначе они гарантированно мёртвые.
  const canDecideBill = canManageBills && dispute.status !== 'OPEN'

  const {
    fileInputRef,
    isUploading,
    handleUploadClick,
    handleFileSelect,
    handleVerifyBill,
    handleRejectBill,
  } = useReceiptActions({
    ticketToken: dispute.token,
    canManageBills,
    onUploaded: useCallback(
      (file: MatchingFile) => onFilesChange(dispute.id, [file]),
      [onFilesChange, dispute.id]
    ),
    // Строка журнала остаётся на месте: решение по чеку не закрывает диспут
    // и не меняет статус тикета.
    onDecided: useCallback(
      (billId: MatchingFile['id'], status: MatchingFile['status']) =>
        onFilesChange(
          dispute.id,
          files.map((f) => (f.id === billId ? { ...f, status } : f))
        ),
      [onFilesChange, dispute.id, files]
    ),
  })

  // Спор о выплате не замораживает чеков и не держит выплату — у его строки нет ни чека, ни удержания.
  const isPayout = isPayoutDispute(dispute)

  return (
    <tr>
      <td className='text-monospace'>
        {isPayout ? <PayoutTokenLink token={dispute.token} className='text-primary text-monospace' /> : dispute.token}
      </td>
      <td className='text-nowrap'>{renderDisputeType(dispute)}</td>
      {/* Длину значения задаёт мерчант — выводим через TruncatedValue, как и остальные
          мерчантские идентификаторы: колонка не разъезжается, полное читается по наведению. */}
      <td>
        <TruncatedValue value={dispute.merchantTransactionId} className='text-monospace' empty='—' />
      </td>
      <td>{dispute.merchantTitle ?? '—'}</td>
      <td>{convertUTCtoMSK(dispute.startedAt)}</td>
      <td>{renderAmount(dispute)}</td>
      <td>
        <DisputeReasonCell dispute={dispute} reasons={reasons} onUpdated={onDisputeUpdated} />
      </td>
      <td className='text-muted'>{renderAutoSituation(dispute)}</td>
      <td>
        <span className={`badge ${DISPUTE_STATUS_BADGE[dispute.status] ?? 'badge-light'}`}>
          {DISPUTE_STATUS_LABEL[dispute.status] ?? dispute.status}
        </span>
      </td>
      <td>
        <span className='badge badge-light'>{dispute.ticketStatus}</span>
      </td>
      <td>{renderCulprits(dispute)}</td>
      <td className='text-nowrap'>{renderChunkHold(dispute)}</td>
      <td className='text-nowrap'>
        <DisputeAutoCancelCountdown autoCloseAt={dispute.autoCloseAt} />
      </td>
      <td className='text-center'>
        {isPayout ? (
          <span className='text-muted'>—</span>
        ) : bill?.url ? (
          <div className='d-flex align-items-center justify-content-center gap-2'>
            <FileDownloadButton
              url={bill.url}
              id={bill.id}
              status={bill.status}
              badgePosition='start'
              badgeStatus={bill.autoCheckStatus}
              badgekMessage={bill.autoCheckMessage}
            />
            <BillStatusBadge
              bill={bill}
              handleVerifyBill={() => handleVerifyBill(bill)}
              handleRejectBill={() => handleRejectBill(bill)}
              canManageBills={canDecideBill}
            />
          </div>
        ) : (
          /* Кнопка только при пустых files — правило «один чек на тикет». */
          <ReceiptUploadButton
            fileInputRef={fileInputRef}
            isUploading={isUploading}
            onClick={handleUploadClick}
            onFileSelect={handleFileSelect}
          />
        )}
      </td>
      <td className={`text-muted ${dispute.createdBy ? 'text-monospace' : ''}`}>
        {renderCreatedBy(dispute)}
      </td>
      <td className={`text-muted ${dispute.resolvedBy ? 'text-monospace' : ''}`}>
        {renderResolvedBy(dispute)}
      </td>
      <td className='text-muted'>{renderComment(dispute.comment)}</td>
      <td className='w-200px'>
        <div className='d-flex align-items-start justify-content-end gap-2'>
          {/* d-grid gap-2: обе кнопки одной ширины и не прыгают при разной длине текста. */}
          <div className='d-grid gap-2' style={{ maxWidth: 180 }}>
            {dispute.canResolveSuccess && (
              <button
                type='button'
                className='btn btn-sm btn-light-success text-nowrap'
                onClick={() => onResolveSuccess(dispute)}
              >
                Завершить успешно
              </button>
            )}
            {dispute.canResolveReject && (
              <button
                type='button'
                className='btn btn-sm btn-light-danger text-nowrap'
                onClick={() => onReject(dispute)}
              >
                Отказать
              </button>
            )}
          </div>
          {/* Слот держит ширину «⋮» и когда меню нет: иначе кнопки решения съезжают вправо. */}
          <RowKebabSlot>
            {canReleaseChunk && (
              <RowKebabMenu title='Действия по диспуту'>
                <Dropdown.Item
                  as='button'
                  type='button'
                  className='d-flex align-items-center'
                  onClick={() => setShowReleaseModal(true)}
                >
                  <KTIcon iconName='lock-3' className='fs-3 me-2' />
                  Освободить депозит
                </Dropdown.Item>
              </RowKebabMenu>
            )}
          </RowKebabSlot>
        </div>
        {showReleaseModal && (
          <ReleaseDepositChunkModal
            show={showReleaseModal}
            onClose={() => setShowReleaseModal(false)}
            depositToken={dispute.token}
            heldUntil={dispute.chunkHeldUntil ?? null}
            onReleased={(heldUntil) => onChunkReleased(dispute.id, heldUntil)}
          />
        )}
      </td>
    </tr>
  )
}
